import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { IPermitTypeField } from '~/client/graph'
import { isFieldMultiple } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import Workflow from '~/client/src/shared/models/Workflow'

import { LinkedWorkflowViewList } from '../../../LinkedWorkflowViewList'
import StruxhubLinkedWorkflowSelector from '../../../StruxhubInputs/StruxhubSelector/StruxhubLinkedWorkflowSelector'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import PermitBaseFormField from './PermitBaseFormField'

interface IProps {
  typeField: IPermitTypeField

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  isViewMode: boolean

  tableRowIndex?: number
  tableId?: string
  parentInstanceId?: string
  parentValueIndex?: number
}

type FieldValue = { id: string; caption?: string }

const PermitFormLinkedWorkflowField: React.FC<IProps> = observer(
  ({
    typeField,
    store,
    fieldsStore,
    isViewMode,
    tableRowIndex,
    tableId,
    parentInstanceId,
    parentValueIndex,
  }) => {
    const { workflowTypesStore } = React.useContext(MobXProviderContext) as any

    const isTableField = !!tableId
    const isMultiple = isFieldMultiple(typeField.isMultiple)

    const getRawValues = () =>
      fieldsStore.getFieldValues<string | Workflow>(
        typeField.id,
        tableId,
        tableRowIndex,
        false,
        parentInstanceId,
        parentValueIndex,
      )

    const getFieldValues = (): FieldValue[] => {
      const values = getRawValues()

      if (!values.length) return [{ id: '', caption: '' }]

      return values
        .filter((value): value is string | Workflow => value != null)
        .map(value => {
          if (typeof value === 'string') return { id: value, caption: '' }

          return {
            id: value.id,
            caption: value.id
              ? ''
              : `New ${value.getCaption(workflowTypesStore)}`,
          }
        })
    }

    // Compute for PermitBaseFormField prop (tracked by our observer reaction)
    const fieldValues = getFieldValues()

    const allowSelectExisting = typeField.allowSelectExisting ?? true
    const allowCreateNew = typeField.allowCreateNew ?? true

    const onWorkflowClick = React.useCallback(
      (workflow: Workflow) => {
        store.openWorkflowHandler?.(workflow)
      },
      [store],
    )

    const onChange = (index: number, value: string | Workflow) => {
      const { changeFieldValue, changeTableFieldValue, addNewFieldValue } =
        fieldsStore

      // Get the ID from the value (could be a string ID or a SitePermit object)
      const valueId = typeof value === 'string' ? value : value?.id

      // Read live from store (this may be called from a stored callback)
      const currentFieldValues = getFieldValues()

      // Check if this workflow is already selected (avoid duplicates)
      const existingIds = currentFieldValues.map(v => v.id).filter(Boolean)
      if (valueId && existingIds.includes(valueId)) {
        return
      }

      // Check if the current index position has an empty value
      // Note: A new workflow with id=null but caption is NOT empty (it's pending creation)
      const currentValue = currentFieldValues[index]
      const isCurrentEmpty = !currentValue?.id && !currentValue?.caption

      if (isTableField) {
        if (isCurrentEmpty) {
          changeTableFieldValue(
            tableId,
            tableRowIndex,
            typeField,
            index,
            value,
            parentInstanceId,
            parentValueIndex,
          )
        }
        // Table field multi-add not supported
        return
      }

      if (isCurrentEmpty) {
        // Overwrite the empty slot at the given index
        changeFieldValue(
          typeField,
          index,
          value,
          parentInstanceId,
          parentValueIndex,
        )
      } else {
        // Add as a new field value
        addNewFieldValue(typeField, value, parentInstanceId, parentValueIndex)
      }
    }

    const onChangeMultiple = (
      values: (string | Workflow)[] | string | Workflow,
    ) => {
      // Table fields always use the table-aware API regardless of isMultiple
      if (isTableField) {
        const normalized = Array.isArray(values) ? values : [values]
        if (normalized.length > 0) {
          fieldsStore.changeTableFieldValue(
            tableId,
            tableRowIndex,
            typeField,
            0,
            normalized[0],
            parentInstanceId,
            parentValueIndex,
          )
        }
        return
      }

      if (!isMultiple) {
        const normalized = Array.isArray(values) ? values : [values]
        fieldsStore.changeMultipleFieldValues(
          typeField,
          normalized,
          parentInstanceId,
          parentValueIndex,
        )
        return
      }

      if (!Array.isArray(values)) {
        const rawValues = getRawValues()
        onChange(rawValues.length, values)
        return
      }

      const rawValues = getRawValues()

      const newWorkflows = rawValues.filter(
        (v): v is Workflow => typeof v !== 'string' && !v?.id,
      )

      const valuesNewWorkflows = values.filter(
        (v): v is Workflow => typeof v !== 'string' && !v?.id,
      )
      const additionalNewWorkflows = newWorkflows.filter(
        nw => !valuesNewWorkflows.includes(nw),
      )

      const combinedValues: (string | Workflow)[] = [
        ...values,
        ...additionalNewWorkflows,
      ]

      fieldsStore.changeMultipleFieldValues(
        typeField,
        combinedValues,
        parentInstanceId,
        parentValueIndex,
      )
    }

    const onOpenWorkflowModal = () => {
      store.setSelectedField(
        typeField,
        0,
        tableId,
        tableRowIndex,
        parentInstanceId,
        parentValueIndex,
      )
      store.setSelectedOnChangeHandler(
        onChangeMultiple as <T>(newValue: T) => void,
      )
    }

    const onRemoveWorkflow = (workflow: Workflow) => {
      if (isTableField) return

      const { rearrangeInvalidState, removeFieldValue } = fieldsStore

      const rawValues = getRawValues()

      let index: number
      if (workflow.id) {
        // Existing workflow: find by ID
        index = rawValues.findIndex(
          v => (typeof v === 'string' ? v : v?.id) === workflow.id,
        )
      } else {
        // New workflow (no ID): find by object reference
        index = rawValues.findIndex(v => v === workflow)
      }

      if (index < 0 || index >= rawValues.length) return

      rearrangeInvalidState(
        typeField.id,
        index,
        parentInstanceId,
        parentValueIndex,
      )
      removeFieldValue(typeField.id, index, parentInstanceId, parentValueIndex)
    }

    const renderComponent = (value: FieldValue, index: number): JSX.Element => {
      if (index !== 0) return null

      // Read from store LIVE so MobX tracks this in the calling component's reaction
      const currentFieldValues = getFieldValues()
      const allIds = currentFieldValues.map(v => v.id).filter(Boolean)

      const rawValues = getRawValues()
      const newWorkflows = rawValues.filter(
        (v): v is Workflow => typeof v !== 'string' && !v?.id,
      )

      return (
        <StruxhubLinkedWorkflowSelector
          withAllDrafts
          workflowIds={allIds}
          newWorkflows={newWorkflows}
          isMultiple={isMultiple}
          onClick={onOpenWorkflowModal}
          onSelectExisting={() => {
            store.setFieldSelectionMode('selectExisting')
            onOpenWorkflowModal()
          }}
          onRemove={onRemoveWorkflow}
          label={typeField.caption}
          isRequired={typeField.isMandatory}
          isViewMode={false}
          allowSelectExisting={allowSelectExisting}
          allowCreateNew={allowCreateNew}
        />
      )
    }

    const renderViewMode = (value: FieldValue): JSX.Element => {
      // Read from store LIVE so MobX tracks this in the calling component's reaction
      const currentFieldValues = getFieldValues()
      const firstValue = currentFieldValues[0]
      if (value.id !== firstValue.id || value.caption !== firstValue.caption) {
        return null
      }

      const allIds = currentFieldValues.map(v => v.id).filter(Boolean)

      if (!allIds.length) {
        return null
      }

      return (
        <LinkedWorkflowViewList
          withAllDrafts
          workflowIds={allIds}
          onWorkflowClick={onWorkflowClick}
        />
      )
    }

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={renderComponent}
        viewModeRenderer={renderViewMode}
      />
    )
  },
)

export default PermitFormLinkedWorkflowField
